import { useTranslation } from 'react-i18next';
import { X, AlertCircle, QrCode, Download, Printer } from 'lucide-react';

interface EmergencyQRProps {
  petName: string;
  petId: string;
  onClose: () => void;
}

export function EmergencyQR({ petName: _petName, petId: _petId, onClose }: EmergencyQRProps) {
  const { t } = useTranslation();
  const emergencyData = {
    petName: 'Max',
    species: 'Dog',
    breed: 'Golden Retriever',
    age: '4 years',
    allergies: ['Chicken', 'Certain antibiotics'],
    medications: ['Antihistamine (as needed)', 'Flea & tick prevention (monthly)'],
    conditions: ['Mild environmental allergies'],
    ownerContact: {
      name: 'John Smith',
      phone: '+1 (555) 123-4567',
      email: 'john.smith@email.com'
    },
    vetContact: {
      name: 'Happy Paws Veterinary Clinic',
      phone: '+1 (555) 987-6543',
      address: '123 Main St, City, State'
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
        <div className="bg-red-600 text-white p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-8 h-8" />
              <div>
                <h2 className="text-2xl mb-1">{t('features.qrHub.emergencyQR.title')}</h2>
                <p className="text-sm opacity-90">{t('features.qrHub.emergencyQR.subtitle')}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Emergency QR Display */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* QR Code */}
            <div className="text-center">
              <div className="w-64 h-64 bg-gradient-to-br from-red-100 to-orange-100 rounded-xl mx-auto flex items-center justify-center shadow-lg mb-4">
                <QrCode className="w-48 h-48 text-red-600" />
              </div>
              
              <div className="space-y-2">
                <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700">
                  <Download className="w-4 h-4" />
                  {t('features.qrHub.emergencyQR.downloadQR')}
                </button>
                <button className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
                  <Printer className="w-4 h-4" />
                  {t('features.qrHub.emergencyQR.printForTag')}
                </button>
              </div>

              <div className="mt-4 text-sm text-gray-600">
                {t('features.qrHub.emergencyQR.printAttachNote')}
              </div>
            </div>

            {/* Emergency Info Preview */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="mb-4">{t('features.qrHub.emergencyQR.whatsIncluded')}</h4>
              
              <div className="space-y-4 text-sm">
                <div>
                  <div className="text-gray-600 mb-1">{t('features.qrHub.emergencyQR.petInformation')}</div>
                  <div>{emergencyData.petName} • {emergencyData.species} • {emergencyData.breed}</div>
                  <div className="text-gray-500">{emergencyData.age}</div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="text-red-600 mb-2">{t('features.qrHub.emergencyQR.criticalAllergies')}</div>
                  {emergencyData.allergies.map((allergy, index) => (
                    <div key={index} className="text-red-700">• {allergy}</div>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="text-gray-600 mb-2">{t('features.qrHub.emergencyQR.currentMedications')}</div>
                  {emergencyData.medications.map((med, index) => (
                    <div key={index}>• {med}</div>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="text-gray-600 mb-2">{t('features.qrHub.emergencyQR.medicalConditions')}</div>
                  {emergencyData.conditions.map((condition, index) => (
                    <div key={index}>• {condition}</div>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="text-gray-600 mb-2">{t('features.qrHub.emergencyQR.ownerContact')}</div>
                  <div>{emergencyData.ownerContact.name}</div>
                  <div className="text-gray-500">{emergencyData.ownerContact.phone}</div>
                  <div className="text-gray-500">{emergencyData.ownerContact.email}</div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="text-gray-600 mb-2">{t('features.qrHub.emergencyQR.veterinarian')}</div>
                  <div>{emergencyData.vetContact.name}</div>
                  <div className="text-gray-500">{emergencyData.vetContact.phone}</div>
                  <div className="text-gray-500 text-xs">{emergencyData.vetContact.address}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Important Notes */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <h5 className="text-red-900">{t('features.qrHub.emergencyQR.emergencyFeatures')}</h5>
              </div>
              <ul className="text-sm text-red-800 space-y-1">
                <li>✓ {t('features.qrHub.emergencyQR.noLoginRequired')}</li>
                <li>✓ {t('features.qrHub.emergencyQR.readOnlyAccess')}</li>
                <li>✓ {t('features.qrHub.emergencyQR.criticalInfoOnly')}</li>
                <li>✓ {t('features.qrHub.emergencyQR.autoLogged')}</li>
                <li>✓ {t('features.qrHub.emergencyQR.ownerNotifiedImmediate')}</li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-2 mb-2">
                <QrCode className="w-5 h-5 text-blue-600 mt-0.5" />
                <h5 className="text-blue-900">{t('features.qrHub.emergencyQR.recommendedUsage')}</h5>
              </div>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• {t('features.qrHub.emergencyQR.printLamate')}</li>
                <li>• {t('features.qrHub.emergencyQR.addLockScreenWidget')}</li>
                <li>• {t('features.qrHub.emergencyQR.shareWithProviders')}</li>
                <li>• {t('features.qrHub.emergencyQR.keepInCar')}</li>
                <li>• {t('features.qrHub.emergencyQR.updateWhenMedsChange')}</li>
              </ul>
            </div>
          </div>

          {/* Privacy & Logging */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-6">
            <h5 className="mb-3">{t('features.qrHub.emergencyQR.privacyLogging')}</h5>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex items-start gap-2">
                <span className="text-purple-600">•</span>
                <span>{t('features.qrHub.emergencyQR.privacyPoint1')}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-purple-600">•</span>
                <span>{t('features.qrHub.emergencyQR.privacyPoint2')}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-purple-600">•</span>
                <span>{t('features.qrHub.emergencyQR.privacyPoint3')}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-purple-600">•</span>
                <span>{t('features.qrHub.emergencyQR.privacyPoint4')}</span>
              </div>
            </div>
          </div>

          {/* Lock Screen Setup Guide */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h5 className="mb-3">{t('features.qrHub.emergencyQR.addToLockScreen')}</h5>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="mb-2">{t('features.qrHub.emergencyQR.iosTitle')}</div>
                <ol className="space-y-1 text-gray-600">
                  <li>{t('features.qrHub.emergencyQR.iosStep1')}</li>
                  <li>{t('features.qrHub.emergencyQR.iosStep2')}</li>
                  <li>{t('features.qrHub.emergencyQR.iosStep3')}</li>
                  <li>{t('features.qrHub.emergencyQR.iosStep4')}</li>
                  <li>{t('features.qrHub.emergencyQR.iosStep5')}</li>
                </ol>
              </div>
              <div>
                <div className="mb-2">{t('features.qrHub.emergencyQR.androidTitle')}</div>
                <ol className="space-y-1 text-gray-600">
                  <li>{t('features.qrHub.emergencyQR.androidStep1')}</li>
                  <li>{t('features.qrHub.emergencyQR.androidStep2')}</li>
                  <li>{t('features.qrHub.emergencyQR.androidStep3')}</li>
                  <li>{t('features.qrHub.emergencyQR.androidStep4')}</li>
                  <li>{t('features.qrHub.emergencyQR.androidStep5')}</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700">
              {t('features.qrHub.emergencyQR.downloadForPrinting')}
            </button>
            <button className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
              {t('features.qrHub.emergencyQR.shareViaEmail')}
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('features.qrHub.emergencyQR.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
